import { useState, useEffect, useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { DocumentReference, ServiceRequest } from '@medplum/fhirtypes';
import { loadDocumentReference } from '@utils/consent.utils';
import { EXTERNAL_CS, SYSTEMS } from '@/consts/systems.consts';

export interface OrderSupportingDocuments {
  orderDocuments: DocumentReference[];
  authDocuments: DocumentReference[];
}

export function useOrderSupportingDocuments(
  serviceRequest: ServiceRequest | null
): OrderSupportingDocuments & {
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [orderDocuments, setOrderDocuments] = useState<DocumentReference[]>([]);
  const [authDocuments, setAuthDocuments] = useState<DocumentReference[]>([]);

  const loadDocuments = useCallback(async () => {
    if (!serviceRequest?.id || !serviceRequest.supportingInfo?.length) {
      setOrderDocuments([]);
      setAuthDocuments([]);
      return;
    }

    setLoading(true);
    try {
      const orderDocs: DocumentReference[] = [];
      const authDocs: DocumentReference[] = [];

      for (const ref of serviceRequest.supportingInfo) {
        const doc = await loadDocumentReference(ref, medplum);
        if (!doc) {
          continue;
        }

        // Skip documents marked as entered-in-error (soft deleted)
        if (doc.status === 'entered-in-error') {
          continue;
        }

        const code = doc.type?.coding?.[0]?.code;
        const system = doc.type?.coding?.[0]?.system;

        if (
          system === SYSTEMS.LOINC &&
          code === EXTERNAL_CS.LOINC.CODES.ORDER_DOCUMENT
        ) {
          orderDocs.push(doc);
        } else if (
          system === SYSTEMS.LOINC &&
          code === EXTERNAL_CS.LOINC.CODES.AUTHORIZATION_DOCUMENT
        ) {
          authDocs.push(doc);
        }
      }

      setOrderDocuments(orderDocs);
      setAuthDocuments(authDocs);
    } catch (error) {
      console.error('Error loading order supporting documents:', error);
      setOrderDocuments([]);
      setAuthDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [serviceRequest?.id, serviceRequest?.supportingInfo, medplum]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  return {
    orderDocuments,
    authDocuments,
    loading,
    refetch: loadDocuments,
  };
}
